import { getIsRecord } from '@aline/common';
import type { Core } from '@pdftron/webviewer';

import { getIsAlineFieldName } from './helpers';

export interface CreateInnerElementOptions {
  readonly annotation: Core.Annotations.WidgetAnnotation;
  readonly originalCreateInnerElement: (annotationManager?: unknown) => HTMLElement;
}

type AlineWidgetInnerElement =
  | {
      readonly el: HTMLElement;
      readonly isAline: false;
    }
  | {
      readonly annotation: Core.Annotations.WidgetAnnotation;
      readonly el: HTMLElement;
      readonly isAline: true;
    };

/**
 * WebViewer calls a custom handler as `handler.apply(this, args.concat(options))`, so its options
 * land last. `DatePickerWidgetAnnotation` calls `super.createInnerElement()` with no arguments,
 * which shifts them into the first parameter. `isShifted` says no manager came through.
 */
export function resolveCreateInnerElementOptions(
  annotationManagerOrOptions: unknown,
  options: unknown
): { readonly isShifted: boolean; readonly options: CreateInnerElementOptions } | null {
  if (getIsCreateInnerElementOptions(annotationManagerOrOptions))
    return { isShifted: true, options: annotationManagerOrOptions };
  if (getIsCreateInnerElementOptions(options)) return { isShifted: false, options };

  return null;
}

export function createAlineWidgetInnerElement(
  annotationManager: unknown,
  options: unknown
): AlineWidgetInnerElement {
  const resolved = resolveCreateInnerElementOptions(annotationManager, options);
  if (!resolved) throw new Error('Expected WebViewer createInnerElement options.');

  const { annotation, originalCreateInnerElement } = resolved.options;
  const el = originalCreateInnerElement(resolved.isShifted ? undefined : annotationManager);
  if (!getIsAlineFieldName(annotation.fieldName)) return { el, isAline: false };
  if (!(el instanceof HTMLElement)) throw new Error('Expected HTML element.');

  return { annotation, el, isAline: true };
}

/**
 * A shape check, not `instanceof`: a false negative is invisible, since the caller throws and the
 * throw is swallowed, leaving the field silently unrendered.
 */
function getIsCreateInnerElementOptions(value: unknown): value is CreateInnerElementOptions {
  if (!getIsRecord(value)) return false;

  return (
    getIsRecord(value['annotation']) && typeof value['originalCreateInnerElement'] === 'function'
  );
}
